import { Navigate } from "react-router-dom";
import { useContext } from "react";
import PropTypes from "prop-types";
import AuthContext from "context/AuthContext";
import useCan from "hooks/useCan";
import PurebrandLoader from "components/Loader/PurebrandLoader";
import { landingPathFor } from "utils/routes";

/**
 * Route guard: renders children only when the user's role holds a capability.
 *
 * Replaces the old AdminRoute, which blocked hosts and nothing else. A user
 * who types a URL they cannot use is sent to their own landing page rather
 * than to a dead end, so the menu and the address bar always agree.
 *
 * Waits for capabilities to be confirmed before deciding, so a cached login
 * payload can never bounce a user off a page they are in fact allowed on.
 *
 * @param {String} capability capability code the route needs
 * @param {String[]} anyOf alternative to `capability`: any one of these
 */
const RequireCapability = ({ capability, anyOf, children }) => {
  const { user, loggedIn } = useContext(AuthContext);
  const { can, canAny, isResolved } = useCan();

  if (!user && !loggedIn) {
    return <Navigate to="/login" />;
  }

  if (!isResolved) {
    return <PurebrandLoader />;
  }

  const allowed = capability ? can(capability) : canAny(anyOf);
  if (!allowed) {
    return <Navigate to={landingPathFor(can)} replace />;
  }

  return children;
};

RequireCapability.propTypes = {
  capability: PropTypes.string,
  anyOf: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
};

export default RequireCapability;
